import type { PodcastEpisode } from "./podcast.types";

export { type PodcastEpisode };

class PodcastState {
	episodes = $state<PodcastEpisode[]>([]);
	currentEpisode = $state<PodcastEpisode | null>(null);
	audio = $state<HTMLAudioElement | null>(null);
	isPlaying = $state(false);
	currentTime = $state(0);
	duration = $state(0);
	volume = $state(1);
	isVisible = $state(false);

	constructor(episodes: PodcastEpisode[] = []) {
		this.episodes = episodes;

		if (typeof window !== "undefined") {
			this.audio = new Audio();
			this.setupAudioListeners();
			(window as any).podcastPlayer = this;
		}
	}

	setEpisodes(episodes: PodcastEpisode[]) {
		this.episodes = episodes;
	}

	private setupAudioListeners() {
		if (!this.audio) return;

		this.audio.addEventListener("loadedmetadata", () => {
			this.duration = this.audio!.duration;
		});

		this.audio.addEventListener("timeupdate", () => {
			this.currentTime = this.audio!.currentTime;
		});

		this.audio.addEventListener("play", () => {
			this.isPlaying = true;
		});

		this.audio.addEventListener("pause", () => {
			this.isPlaying = false;
		});

		this.audio.addEventListener("ended", () => {
			this.isPlaying = false;
			this.currentTime = 0;
		});

		this.audio.addEventListener("error", (e) => {
			console.error("Audio error:", e);
			this.isPlaying = false;
		});
	}

	playEpisode(episode: PodcastEpisode) {
		this.currentEpisode = episode;
		this.isVisible = true;

		if (this.audio) {
			this.audio.src = episode.audioUrl;
			this.audio.load();
			this.audio.play().catch((error) => {
				console.error("Error playing audio:", error);
			});
		}
	}

	togglePlay() {
		if (!this.audio || !this.currentEpisode) return;

		if (this.isPlaying) {
			this.audio.pause();
		} else {
			this.audio.play().catch((error) => {
				console.error("Error playing audio:", error);
			});
		}
	}

	skipBackward() {
		if (this.audio) {
			this.audio.currentTime = Math.max(0, this.audio.currentTime - 15);
		}
	}

	skipForward() {
		if (this.audio) {
			this.audio.currentTime = Math.min(this.duration, this.audio.currentTime + 15);
		}
	}

	seekTo(percentage: number) {
		if (this.audio) {
			this.audio.currentTime = (percentage / 100) * this.duration;
		}
	}

	setVolume(volumePercentage: number) {
		this.volume = volumePercentage / 100;
		if (this.audio) {
			this.audio.volume = this.volume;
		}
	}

	formatTime(seconds: number): string {
		if (isNaN(seconds)) return "0:00";
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}

	closePlayer() {
		if (this.audio) {
			this.audio.pause();
		}
		this.isVisible = false;
		this.currentEpisode = null;
	}

	get progressPercentage(): number {
		if (this.duration === 0) return 0;
		return (this.currentTime / this.duration) * 100;
	}

	get volumePercentage(): number {
		return this.volume * 100;
	}

	destroy() {
		if (this.audio) {
			this.audio.pause();
			this.audio.src = "";
			this.audio = null;
		}
	}
}

export function createPodcastState(episodes: PodcastEpisode[] = []) {
	return new PodcastState(episodes);
}

export const podcastState = new PodcastState();
